import { useState } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import { FaArrowLeft, FaCheck, FaTimes } from "react-icons/fa";
import { SiSnowflake } from "react-icons/si";

type SnowflakeFormData = {
  name: string;
  accountUrl: string;
  database: string;
  schema: string;
  warehouse: string;
  role: string;
  username: string;
  password: string;
};

type FormErrors = {
  [key in keyof SnowflakeFormData]?: string;
};

const SnowflakeConnection = () => {
  const navigate = useNavigate();
  const { addConnection } = useGlobalStore((state) => state);

  const [formData, setFormData] = useState<SnowflakeFormData>({
    name: "",
    accountUrl: "",
    database: "",
    schema: "PUBLIC",
    warehouse: "",
    role: "",
    username: "",
    password: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(
    null
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate connection name
    if (!formData.name.trim()) {
      newErrors.name = "Connection name is required";
    } else if (formData.name.length < 3 || formData.name.length > 50) {
      newErrors.name = "Connection name must be between 3 and 50 characters";
    }

    // Validate account URL
    if (!formData.accountUrl.trim()) {
      newErrors.accountUrl = "Account URL is required";
    } else if (!formData.accountUrl.includes(".snowflakecomputing.com")) {
      newErrors.accountUrl =
        "Must be a valid Snowflake account URL (e.g., account.snowflakecomputing.com)";
    }

    // Validate database
    if (!formData.database.trim()) {
      newErrors.database = "Database is required";
    }

    // Validate schema
    if (!formData.schema.trim()) {
      newErrors.schema = "Schema is required";
    }

    // Validate warehouse
    if (!formData.warehouse.trim()) {
      newErrors.warehouse = "Warehouse is required";
    }

    // Validate username
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    // Validate password
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      // TODO: Call API to test connection
      // For now, simulate a delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Simulate success for demo
      setTestResult("success");
    } catch (error) {
      setTestResult("error");
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare connection data
      const connectionData = {
        name: formData.name,
        connection_type: "snowflake",
        host: formData.accountUrl,
        database: formData.database,
        schema: formData.schema,
        warehouse: formData.warehouse,
        role: formData.role || undefined,
        username: formData.username,
        password: formData.password,
      };

      await addConnection(connectionData);
      navigate("/connections");
    } catch (error) {
      console.error("Failed to save connection:", error);
      // TODO: Show error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Back Navigation */}
      <button
        onClick={() => navigate("/connections")}
        className="btn btn-ghost mb-6"
      >
        <FaArrowLeft className="mr-2" />
        Back to Connections
      </button>

      {/* Page Header */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-cyan-100 rounded-lg">
              <SiSnowflake className="w-8 h-8 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">New Snowflake Connection</h1>
              <p className="text-base-content/70">
                Configure your Snowflake data warehouse
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 uppercase text-base-content/80">
                Basic Information
              </h2>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Connection Name</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Analytics Warehouse"
                  className={`input input-bordered ${
                    errors.name ? "input-error" : ""
                  }`}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name}
                    </span>
                  </label>
                )}
              </div>
            </div>

            {/* Snowflake Account Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 uppercase text-base-content/80">
                Snowflake Account
              </h2>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Account URL</span>
                </label>
                <input
                  type="text"
                  name="accountUrl"
                  value={formData.accountUrl}
                  onChange={handleInputChange}
                  placeholder="account.snowflakecomputing.com"
                  className={`input input-bordered ${
                    errors.accountUrl ? "input-error" : ""
                  }`}
                />
                {errors.accountUrl && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.accountUrl}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Your full Snowflake account URL
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Database</span>
                  </label>
                  <input
                    type="text"
                    name="database"
                    value={formData.database}
                    onChange={handleInputChange}
                    placeholder="MY_DATABASE"
                    className={`input input-bordered ${
                      errors.database ? "input-error" : ""
                    }`}
                  />
                  {errors.database && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.database}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Schema</span>
                  </label>
                  <input
                    type="text"
                    name="schema"
                    value={formData.schema}
                    onChange={handleInputChange}
                    placeholder="PUBLIC"
                    className={`input input-bordered ${
                      errors.schema ? "input-error" : ""
                    }`}
                  />
                  {errors.schema && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.schema}
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Warehouse & Role Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 uppercase text-base-content/80">
                Warehouse & Role
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">
                      Warehouse <span className="text-error">*</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    name="warehouse"
                    value={formData.warehouse}
                    onChange={handleInputChange}
                    placeholder="COMPUTE_WH"
                    className={`input input-bordered ${
                      errors.warehouse ? "input-error" : ""
                    }`}
                  />
                  {errors.warehouse && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.warehouse}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Role (optional)</span>
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    placeholder="ACCOUNTADMIN"
                    className="input input-bordered"
                  />
                  <label className="label">
                    <span className="label-text-alt text-base-content/60">
                      Leave blank to use default role
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Authentication Section */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 uppercase text-base-content/80">
                Authentication
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Username</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="your_username"
                    className={`input input-bordered ${
                      errors.username ? "input-error" : ""
                    }`}
                  />
                  {errors.username && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.username}
                      </span>
                    </label>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Password</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className={`input input-bordered ${
                      errors.password ? "input-error" : ""
                    }`}
                  />
                  {errors.password && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.password}
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Test Result Message */}
            {testResult && (
              <div
                className={`alert ${
                  testResult === "success" ? "alert-success" : "alert-error"
                } mb-6`}
              >
                {testResult === "success" ? (
                  <>
                    <FaCheck className="w-5 h-5" />
                    <span>Connection successful!</span>
                  </>
                ) : (
                  <>
                    <FaTimes className="w-5 h-5" />
                    <span>Connection failed. Check your credentials.</span>
                  </>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={isTesting || isSubmitting}
                className="btn btn-outline"
              >
                {isTesting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Testing...
                  </>
                ) : (
                  "Test Connection"
                )}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isTesting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SnowflakeConnection;
